/**
 * Wire grammar for "these epochs, of these campaigns" — `campaign:from-to` / `campaign:id`,
 * comma separated (e.g. `psg-tte:0-25,juv-tte:3`).
 *
 * A multi-campaign dashboard cannot express a partial selection with a flat epoch-id list: the
 * server would apply `campaign IN (…) AND epoch IN (…)`, a cross product that also selects epoch 3
 * of every other campaign. Scoping each id to its campaign keeps the whole selection in ONE query,
 * which is what makes the trader count a `COUNT(DISTINCT hubUserId)` over the union rather than a
 * sum of per-campaign counts — the same wallet trading five campaigns is one trader, not five.
 *
 * Contiguous ids collapse into ranges because the selection UI filters by epoch/date RANGE, so a
 * campaign with a thousand epochs is normally one token. That keeps the query string bounded by the
 * number of runs the user actually picked instead of by the epoch count.
 *
 * The separator is `:` — campaign names use `[a-z0-9-]`, so neither the name nor the `-` inside a
 * range is ambiguous as long as the name is taken up to the FIRST `:`.
 */
export interface CampaignEpochRange {
  campaignName: string;
  /** Inclusive. */
  from: number;
  /** Inclusive; equals `from` for a single epoch. */
  to: number;
}

// The name capture excludes `:` so it ends at the FIRST colon. A greedy `.+` would instead swallow
// one, reading `psg-tte:0:1` as the campaign "psg-tte:0" — a name that matches nothing, so the
// request would come back empty rather than rejected.
const RANGE_TOKEN = /^([^:]+):(\d+)(?:-(\d+))?$/;

/** True when the value uses the campaign-scoped grammar rather than a flat id list. */
export function isCampaignScopedEpochSelection(raw: string): boolean {
  return raw.includes(":");
}

/**
 * Collapses per-campaign epoch ids into the wire form. Ids are sorted and de-duplicated, so callers
 * can pass selection state as-is.
 */
export function encodeCampaignEpochSelection(epochIdsByCampaign: Record<string, number[]>): string {
  const tokens: string[] = [];

  for (const [campaignName, epochIds] of Object.entries(epochIdsByCampaign)) {
    const sorted = [...new Set(epochIds)].sort((a, b) => a - b);
    if (sorted.length === 0) continue;

    let runStart = sorted[0];
    let runEnd = sorted[0];

    const flush = () => {
      tokens.push(runStart === runEnd ? `${campaignName}:${runStart}` : `${campaignName}:${runStart}-${runEnd}`);
    };

    for (const epochId of sorted.slice(1)) {
      if (epochId === runEnd + 1) {
        runEnd = epochId;
        continue;
      }
      flush();
      runStart = epochId;
      runEnd = epochId;
    }
    flush();
  }

  return tokens.join(",");
}

/**
 * Parses the wire form. Throws on a malformed token rather than dropping it — a silently discarded
 * range would widen the result set into "more epochs than the user picked", which reads as a
 * plausible number and hides the bad input.
 */
export function parseCampaignEpochSelection(raw: string): CampaignEpochRange[] {
  const ranges: CampaignEpochRange[] = [];

  for (const token of raw.split(",")) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    const match = RANGE_TOKEN.exec(trimmed);
    if (!match) {
      throw new Error(
        `Malformed campaign epoch selection token: "${trimmed}" (expected "campaign:id" or "campaign:from-to")`,
      );
    }

    const [, campaignName, fromRaw, toRaw] = match;
    const from = Number.parseInt(fromRaw, 10);
    const to = toRaw === undefined ? from : Number.parseInt(toRaw, 10);

    if (to < from) {
      throw new Error(`Inverted campaign epoch range: "${trimmed}"`);
    }

    ranges.push({ campaignName, from, to });
  }

  return ranges;
}
